import React, { useState, useEffect, useCallback, memo, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ProductSearchInput, { productLabel } from '../Form/ProductSearchInput';
import './EditableSalesOrderLines.css';

// Memoized table row component - only re-renders when its specific line changes
const SalesOrderLineRow = memo(({
  line,
  index,
  editMode,
  loading,
  onLineChange,
  onSaveLine,
  onRemoveLine
}) => {
  const { t } = useTranslation('sales');
  // Calculate line total
  const lineTotal = useMemo(() => {
    const quantity = parseFloat(line.quantity_ordered || 0);
    const unitPrice = parseFloat(line.unit_price || 0);
    const discountPct = parseFloat(line.discount_percentage || 0);
    const taxPct = parseFloat(line.tax_percentage || 0);

    const subtotal = quantity * unitPrice;
    const discount = subtotal * (discountPct / 100);
    const afterDiscount = subtotal - discount;
    const tax = afterDiscount * (taxPct / 100);

    return afterDiscount + tax;
  }, [line.quantity_ordered, line.unit_price, line.discount_percentage, line.tax_percentage]);

  // The line keeps the chosen product's code and name, so the label never depends on a search result (#349)
  const selectedLabel = line.product_code ? productLabel(line) : (line.product_name || '');

  return (
    <tr className={line.isNew ? 'new-line' : line.isModified ? 'modified-line' : ''}>
      <td>
        {editMode ? (
          <input
            type="number"
            value={line.line_number || index + 1}
            onChange={(e) => onLineChange(index, 'line_number', e.target.value)}
            className="form-input-inline"
            style={{ width: '50px' }}
          />
        ) : (
          line.line_number || index + 1
        )}
      </td>
      <td>
        {editMode ? (
          <ProductSearchInput
            value={line.product_id}
            selectedLabel={selectedLabel}
            onSelect={(product) => onLineChange(
              index, 'product_id', product ? (product.product_id ?? product.id) : '', product
            )}
            placeholder={t('editableSalesOrderLines.placeholders.searchProducts')}
            ariaLabel={`${t('common:ui.product')} ${index + 1}`}
          />
        ) : (
          selectedLabel || line.description || '-'
        )}
      </td>
      <td>
        {editMode ? (
          <input
            type="text"
            value={line.description || ''}
            onChange={(e) => onLineChange(index, 'description', e.target.value)}
            className="form-input-inline"
            placeholder={t('editableSalesOrderLines.placeholders.lineDescription')}
          />
        ) : (
          line.description || '-'
        )}
      </td>
      <td>
        {editMode ? (
          <input
            type="number"
            value={line.quantity_ordered ?? ''}
            onChange={(e) => onLineChange(index, 'quantity_ordered', e.target.value)}
            className="form-input-inline"
            step="any"
            min="0"
            required
            aria-label={t('invoices.view.fields.quantity')}
            style={{ width: '100px' }}
          />
        ) : (
          parseFloat(line.quantity_ordered || 0).toLocaleString()
        )}
      </td>
      {!editMode && (
        <td>
          {parseFloat(line.quantity_allocated || 0).toLocaleString()}
        </td>
      )}
      {!editMode && (
        <td>
          <span className={`allocation-status ${
            (line.quantity_allocated || 0) >= (line.quantity_ordered || 0) ? 'allocated' :
            (line.quantity_allocated || 0) > 0 ? 'partial' : 'unallocated'
          }`}>
            {(line.quantity_allocated || 0) >= (line.quantity_ordered || 0) ? t('editableSalesOrderLines.allocation.allocated') :
             (line.quantity_allocated || 0) > 0 ? t('editableSalesOrderLines.allocation.partial') : t('editableSalesOrderLines.allocation.unallocated')}
          </span>
        </td>
      )}
      <td>
        {editMode ? (
          <input
            type="number"
            value={line.unit_price ?? ''}
            onChange={(e) => onLineChange(index, 'unit_price', e.target.value)}
            className="form-input-inline"
            step="any"
            aria-label={t('common:ui.unitPrice')}
            min="0"
            required
            style={{ width: '100px' }}
          />
        ) : (
          `$${parseFloat(line.unit_price || 0).toFixed(2)}`
        )}
      </td>
      <td>
        {editMode ? (
          <input
            type="number"
            value={line.discount_percentage || ''}
            onChange={(e) => onLineChange(index, 'discount_percentage', e.target.value)}
            className="form-input-inline"
            step="0.01"
            min="0"
            max="100"
            style={{ width: '70px' }}
          />
        ) : (
          `${parseFloat(line.discount_percentage || 0).toFixed(1)}%`
        )}
      </td>
      <td>
        {editMode ? (
          <input
            type="number"
            value={line.tax_percentage || ''}
            onChange={(e) => onLineChange(index, 'tax_percentage', e.target.value)}
            className="form-input-inline"
            step="0.01"
            min="0"
            style={{ width: '70px' }}
          />
        ) : (
          `${parseFloat(line.tax_percentage || 0).toFixed(1)}%`
        )}
      </td>
      <td className="text-right">
        <strong>${lineTotal.toFixed(2)}</strong>
      </td>
      {editMode && (
        <td>
          <div className="action-buttons">
            {(line.isNew || line.isModified) && (
              <button
                onClick={() => onSaveLine(index, line)}
                className="btn-action save"
                title={t('common:ui.save')}
                disabled={loading}
              >
                ✓
              </button>
            )}
            <button
              onClick={() => onRemoveLine(index, line)}
              className="btn-action delete"
              title={t('common:ui.remove')}
              disabled={loading}
            >
              ×
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memo
  return (
    prevProps.line === nextProps.line &&
    prevProps.index === nextProps.index &&
    prevProps.editMode === nextProps.editMode &&
    prevProps.loading === nextProps.loading
  );
});

SalesOrderLineRow.displayName = 'SalesOrderLineRow';

const EditableSalesOrderLines = ({ data, parentEntity, editMode, onLinesChange }) => {
  const { t } = useTranslation('sales');
  const orderId = parentEntity?.id || parentEntity?.sales_order_id || parentEntity?.order_id;
  const [localLines, setLocalLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error] = useState(null);
  const isInitialMount = useRef(true);
  const onLinesChangeRef = useRef(onLinesChange);

  // Update the ref when onLinesChange changes
  useEffect(() => {
    onLinesChangeRef.current = onLinesChange;
  }, [onLinesChange]);

  // Initialize lines
  useEffect(() => {
    if (data && data.length > 0) {
      setLocalLines(data.map(line => ({ ...line, isModified: false, isNew: false })));
    } else {
      setLocalLines([]);
    }
    isInitialMount.current = false;
  }, [data]);

  // Handle line changes - notify parent after state update
  const handleLineChange = (index, field, value, productData = null) => {
    setLocalLines(prev => {
      const newLines = [...prev];
      newLines[index] = {
        ...newLines[index],
        [field]: value,
        isModified: !newLines[index].isNew
      };

      // Auto-populate product details when product is selected
      if (field === 'product_id' && !value) {
        newLines[index] = { ...newLines[index], product_code: '', product_name: '' };
      } else if (field === 'product_id' && productData) {
        newLines[index] = {
          ...newLines[index],
          product_name: productData.product_name,
          product_code: productData.product_code,
          unit_price: productData.base_price || productData.standard_price || 0,
          uom_code: productData.unit_of_measure || newLines[index].uom_code,
          description: newLines[index].description || productData.description || productData.product_name
        };
      }

      // Notify parent with updated lines
      setTimeout(() => {
        if (onLinesChangeRef.current && !isInitialMount.current) {
          onLinesChangeRef.current(newLines);
        }
      }, 0);

      return newLines;
    });
  };

  // Parent notification is now handled directly in action functions
  // using setTimeout to avoid infinite loops

  // Add new line
  const handleAddLine = () => {
    setLocalLines(prev => {
      const newLine = {
        temp_id: `new_${Date.now()}`,
        line_number: prev.length + 1,
        product_id: '',
        description: '',
        quantity_ordered: 1,
        unit_price: 0,
        discount_percentage: 0,
        tax_percentage: 0,
        total_price: 0,
        isNew: true,
        isModified: false
      };

      const newLines = [...prev, newLine];

      // Notify parent with updated lines
      setTimeout(() => {
        if (onLinesChangeRef.current && !isInitialMount.current) {
          onLinesChangeRef.current(newLines);
        }
      }, 0);

      return newLines;
    });
  };

  // Save line (placeholder - actual save handled by parent)
  const handleSaveLine = (index, line) => {
    // In a real implementation, this would call an API
    // For now, just mark as saved
    setLocalLines(prev => {
      const newLines = [...prev];
      newLines[index] = {
        ...newLines[index],
        isModified: false,
        isNew: false
      };
      return newLines;
    });
  };

  // Remove line
  const handleRemoveLine = (index, line) => {
    setLocalLines(prev => {
      const newLines = prev.filter((_, i) => i !== index);

      // Notify parent with updated lines
      setTimeout(() => {
        if (onLinesChangeRef.current && !isInitialMount.current) {
          onLinesChangeRef.current(newLines);
        }
      }, 0);

      return newLines;
    });
  };

  // Calculate totals
  const totals = useMemo(() => {
    return localLines.reduce((acc, line) => {
      const quantity = parseFloat(line.quantity_ordered || 0);
      const unitPrice = parseFloat(line.unit_price || 0);
      const discountPct = parseFloat(line.discount_percentage || 0);
      const taxPct = parseFloat(line.tax_percentage || 0);

      const subtotal = quantity * unitPrice;
      const discount = subtotal * (discountPct / 100);
      const afterDiscount = subtotal - discount;
      const tax = afterDiscount * (taxPct / 100);
      const lineTotal = afterDiscount + tax;

      acc.subtotal += subtotal;
      acc.discount += discount;
      acc.tax += tax;
      acc.total += lineTotal;

      return acc;
    }, { subtotal: 0, discount: 0, tax: 0, total: 0 });
  }, [localLines]);

  return (
    <div className="editable-sales-order-lines">
      <div className="lines-header">
        <h3>{t('editableSalesOrderLines.title')}</h3>
        {editMode && (
          <button
            className="btn-primary btn-sm"
            onClick={handleAddLine}
            disabled={loading}
          >
            + {t('invoices.view.buttons.addLine')}
          </button>
        )}
      </div>

      {error && (
        <div className="error-message">{typeof error === 'string' ? error : t('editableSalesOrderLines.errors.generic')}</div>
      )}

      {localLines.length === 0 ? (
        <div className="no-lines">
          <p>{t('editableSalesOrderLines.noLines')}</p>
          {editMode && (
            <button className="btn-primary" onClick={handleAddLine}>
              {t('editableSalesOrderLines.addFirstLine')}
            </button>
          )}
        </div>
      ) : (
        <div className="lines-table-wrapper">
          <table className="lines-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>#</th>
                <th style={{ width: '200px' }}>{t('common:ui.product')}</th>
                <th>{t('common:ui.description')}</th>
                <th style={{ width: '80px' }}>{t('invoices.view.fields.quantity')}</th>
                {!editMode && <th style={{ width: '80px' }}>{t('editableSalesOrderLines.allocation.allocated')}</th>}
                {!editMode && <th style={{ width: '80px' }}>{t('common:ui.status')}</th>}
                <th style={{ width: '100px' }}>{t('common:ui.unitPrice')}</th>
                <th style={{ width: '80px' }}>{t('invoices.view.fields.discount')}</th>
                <th style={{ width: '80px' }}>{t('invoices.view.fields.tax')}</th>
                <th style={{ width: '120px' }}>{t('common:ui.total')}</th>
                {editMode && <th style={{ width: '80px' }}>{t('common:ui.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {localLines.map((line, index) => (
                <SalesOrderLineRow
                  key={line.sales_order_line_id || line.temp_id || index}
                  line={line}
                  index={index}
                  editMode={editMode}
                  loading={loading}
                  onLineChange={handleLineChange}
                  onSaveLine={handleSaveLine}
                  onRemoveLine={handleRemoveLine}
                />
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan={editMode ? 7 : 8} className="text-right">
                  <strong>{t('invoices.view.fields.subtotal')}:</strong>
                </td>
                <td className="text-right">
                  <strong>${totals.subtotal.toFixed(2)}</strong>
                </td>
                {editMode && <td></td>}
              </tr>
              {totals.discount > 0 && (
                <tr className="total-row">
                  <td colSpan={editMode ? 7 : 8} className="text-right">
                    {t('invoices.view.fields.discount')}:
                  </td>
                  <td className="text-right">
                    -${totals.discount.toFixed(2)}
                  </td>
                  {editMode && <td></td>}
                </tr>
              )}
              {totals.tax > 0 && (
                <tr className="total-row">
                  <td colSpan={editMode ? 7 : 8} className="text-right">
                    {t('invoices.view.fields.tax')}:
                  </td>
                  <td className="text-right">
                    ${totals.tax.toFixed(2)}
                  </td>
                  {editMode && <td></td>}
                </tr>
              )}
              <tr className="total-row grand-total">
                <td colSpan={editMode ? 7 : 8} className="text-right">
                  <strong>{t('common:ui.total')}:</strong>
                </td>
                <td className="text-right">
                  <strong className="total-amount">${totals.total.toFixed(2)}</strong>
                </td>
                {editMode && <td></td>}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default EditableSalesOrderLines;